import React, { useState, useRef } from "react";
import PropTypes from "prop-types";

import { Modal, Button, Icon } from "semantic-ui-react";

import FlowFormResponse from "@Components/FlowFormResponse";

import styles from "./styles.module.css";

const WIDGET_ICONS = {
	textarea: "align left",
	email: "envelope",
	phone: "phone",
	mobile: "mobile",
	number: "calculator",
	date: "calendar",
	dropdown: "dropdown",
	select: "dropdown",
	document: "file image",
	file: "file",
	attachment: "paperclip",
	image: "file image",
	checkbox: "check square",
	address: "map marker",
	url: "link",
	time: "clock",
	signature: "pencil",
	currency: "money",
	name: "user",
	status: "info circle",
	notes: "sticky note",
	description: "align justify",
	category: "tag",
	reference: "hashtag",
};

const getIcon = (widget) => {
	const key = String(widget || "")
		.toLowerCase()
		.replace(/[\s_-]+/g, "");
	return WIDGET_ICONS[key] || "";
};

const parseInteractive = (text) => {
	try {
		const parsed = typeof text === "string" ? JSON.parse(text) : text;
		if (parsed && parsed.interactive) return parsed.interactive;
		return parsed;
	} catch (e) {
		return null;
	}
};

const shouldShow = (field, allFields) => {
	if (!field.show) return true;
	const refField = allFields.find((f) => {
		return f.name === field.show.field;
	});
	if (!refField) return true;
	return String(refField.value) === String(field.show.value);
};

const FieldValue = ({ field }) => {
	if (field.widget === "radio" || field.widget === "dropdown") {
		const opt = (field.options || []).find((o) => {
			return o.value === field.value;
		});
		return <span className="flow-field-value">{opt ? opt.label : field.value || ""}</span>;
	}
	return <span className="flow-field-value">{field.value ?? ""}</span>;
};

FieldValue.propTypes = {
	field: PropTypes.object,
};

const InteractiveButton = ({ interactive }) => {
	const body = interactive.body?.text || "";
	const footer = interactive.footer?.text || "";
	const header = interactive.header?.text || "";
	const buttons = interactive.action?.buttons || [];
	return (
		<div className={styles.interactive}>
			<div className={styles.typeBadge}>
				<Icon name="thumb tack" size="small" /> Botones
			</div>
			{header && <div className={styles.interactiveHeader}>{header}</div>}
			{body && <div className={styles.interactiveBody}>{body}</div>}
			{footer && <div className={styles.interactiveFooter}>{footer}</div>}
			<div className={styles.interactiveButtons}>
				{buttons.map((btn, i) => {
					return (
						<div key={i} className={styles.interactiveBtn}>
							{btn.reply?.title || "Button"}
						</div>
					);
				})}
			</div>
		</div>
	);
};

InteractiveButton.propTypes = { interactive: PropTypes.object };

const InteractiveList = ({ interactive }) => {
	const body = interactive.body?.text || "";
	const footer = interactive.footer?.text || "";
	const header = interactive.header?.text || "";
	const buttonLabel = interactive.action?.button || "Menu";
	const sections = interactive.action?.sections || [];
	return (
		<div className={styles.interactive}>
			<div className={styles.typeBadge}>
				<Icon name="list" size="small" /> Lista
			</div>
			{header && <div className={styles.interactiveHeader}>{header}</div>}
			{body && <div className={styles.interactiveBody}>{body}</div>}
			{footer && <div className={styles.interactiveFooter}>{footer}</div>}
			<div className={styles.sectionDivider} />
			<div style={{ padding: "0 8px" }}>
				{sections.map((sec, i) => {
					return (
						<div key={i} className={styles.interactiveSection}>
							{sec.title && <div className={styles.sectionTitle}>{sec.title}</div>}
							{(sec.rows || []).map((row, j) => {
								return (
									<div key={j} className={styles.interactiveRow}>
										<span className={styles.rowTitle}>{row.title}</span>
										{row.description && <span className={styles.rowDesc}>{row.description}</span>}
									</div>
								);
							})}
						</div>
					);
				})}
			</div>
			<div className={styles.interactiveListBtn}>
				<Icon name="chevron down" size="small" /> {buttonLabel}
			</div>
		</div>
	);
};

InteractiveList.propTypes = { interactive: PropTypes.object };

const InteractiveProduct = ({ interactive }) => {
	const body = interactive.body?.text || "";
	const footer = interactive.footer?.text || "";
	const catalogId = interactive.action?.catalog_id || "";
	const productId = interactive.action?.product_retailer_id || "";
	return (
		<div className={styles.interactive}>
			<div className={styles.typeBadge}>
				<Icon name="shopping cart" size="small" /> Producto
			</div>
			{body && <div className={styles.interactiveBody}>{body}</div>}
			{footer && <div className={styles.interactiveFooter}>{footer}</div>}
			<div className={styles.productMeta}>
				<span>
					<Icon name="barcode" size="small" /> {catalogId}
				</span>
				<span>
					<Icon name="cube" size="small" /> {productId}
				</span>
			</div>
		</div>
	);
};

InteractiveProduct.propTypes = { interactive: PropTypes.object };

const InteractiveProductList = ({ interactive }) => {
	const body = interactive.body?.text || "";
	const footer = interactive.footer?.text || "";
	const header = interactive.header?.text || "";
	const sections = interactive.action?.sections || [];
	return (
		<div className={styles.interactive}>
			<div className={styles.typeBadge}>
				<Icon name="th large" size="small" /> Catalogo
			</div>
			{header && <div className={styles.interactiveHeader}>{header}</div>}
			{body && <div className={styles.interactiveBody}>{body}</div>}
			{footer && <div className={styles.interactiveFooter}>{footer}</div>}
			<div className={styles.sectionDivider} />
			{sections.map((sec, i) => {
				return (
					<div key={i} className={styles.interactiveSection}>
						{sec.title && <div className={styles.sectionTitle}>{sec.title}</div>}
						<div className={styles.productItems}>
							{(sec.product_items || []).map((item, j) => {
								return (
									<div key={j} className={styles.productItem}>
										<Icon name="cube" size="small" /> {item.product_retailer_id}
									</div>
								);
							})}
						</div>
					</div>
				);
			})}
		</div>
	);
};

InteractiveProductList.propTypes = { interactive: PropTypes.object };

const InteractiveCTAUrl = ({ interactive }) => {
	const body = interactive.body?.text || "";
	const footer = interactive.footer?.text || "";
	const header = interactive.header?.text || "";
	const displayText = interactive.action?.parameters?.display_text || "Abrir enlace";
	return (
		<div className={styles.interactive}>
			<div className={styles.typeBadge}>
				<Icon name="external" size="small" /> Enlace
			</div>
			{header && <div className={styles.interactiveHeader}>{header}</div>}
			{body && <div className={styles.interactiveBody}>{body}</div>}
			{footer && <div className={styles.interactiveFooter}>{footer}</div>}
			<div className={styles.interactiveCtaBtn}>
				<Icon name="external" size="small" /> {displayText}
			</div>
		</div>
	);
};

InteractiveCTAUrl.propTypes = { interactive: PropTypes.object };

const InteractiveFlow = ({ interactive }) => {
	const body = interactive.body?.text || "";
	const footer = interactive.footer?.text || "";
	const header = interactive.header?.text || "";
	const flowCta = interactive.action?.parameters?.flow_cta || "Abrir Flow";
	const flowId = interactive.action?.parameters?.flow_id || "";
	return (
		<div className={styles.interactive}>
			<div className={styles.typeBadge}>
				<Icon name="tasks" size="small" /> Flow {flowId}
			</div>
			{header && <div className={styles.interactiveHeader}>{header}</div>}
			{body && <div className={styles.interactiveBody}>{body}</div>}
			{footer && <div className={styles.interactiveFooter}>{footer}</div>}
			<div className={styles.interactiveFlowBtn}>
				<Icon name="tasks" size="small" /> {flowCta}
			</div>
		</div>
	);
};

InteractiveFlow.propTypes = { interactive: PropTypes.object };

const FlowPrintModal = ({ fields, title, onClose }) => {
	const printRef = useRef(null);

	const handlePrint = () => {
		const el = printRef.current;
		if (!el) return;

		const now = new Date();
		const dateStr = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

		const blocksHtml = [];
		el.querySelectorAll(".flow-block").forEach((b) => {
			const clone = b.cloneNode(true);
			clone.querySelectorAll(".flow-no-print").forEach((e) => {
				return e.remove();
			});
			blocksHtml.push(clone.outerHTML);
		});

		const html = `<!DOCTYPE html>
<html>
<head>
    <title>${title}</title>
    <style>
        body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 30px 40px; background: #fff; margin: 0; color: #1a1a1a; }
        .flow-print-header { text-align: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #184788; }
        .flow-print-title { font-size: 20px; font-weight: 700; color: #184788; }
        .flow-print-meta { font-size: 11px; color: #6c757d; margin-top: 6px; }
        .flow-block { margin-bottom: 24px; }
        .flow-block-title { font-size: 15px; color: #184788; border-bottom: 2px solid #e8eef6; padding-bottom: 8px; margin-bottom: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
        .flow-table { width: 100%; border-collapse: collapse; }
        .flow-table tr { border-bottom: 1px solid #f0f0f0; }
        .flow-table tr:last-child { border-bottom: none; }
        .flow-table td { padding: 10px 8px; vertical-align: top; font-size: 13px; }
        .flow-label { width: 35%; color: #555; font-weight: 600; }
        .flow-colon { width: 20px; text-align: center; color: #999; }
        .flow-data { color: #1a1a1a; }
        .flow-field-value { word-break: break-word; background: #f7f8fa; padding: 6px 10px; border-radius: 4px; display: inline-block; }
    </style>
</head>
<body>
    <div class="flow-print-header">
        <div class="flow-print-title">${title}</div>
        <div class="flow-print-meta">Impreso: ${dateStr}</div>
    </div>
    ${blocksHtml.join("")}
</body>
</html>`;

		const win = window.open("", "_blank");
		if (!win) {
			return;
		}
		win.document.write(html);
		win.document.close();
		setTimeout(() => {
			win.focus();
			win.print();
			win.close();
		}, 200);
	};

	const bodyFields = fields.filter((f) => {
		return f.widget !== "header";
	});
	const blockMap = {};
	bodyFields.forEach((f) => {
		const block = f.block || "General";
		if (!blockMap[block]) blockMap[block] = [];
		blockMap[block].push(f);
	});

	return (
		<Modal open onClose={onClose} size="large" closeIcon centered>
			<Modal.Header className={styles.modalHeader}>
				<span className={styles.modalHeaderIcon}>
					<Icon name="tasks" />
				</span>
				{title}
			</Modal.Header>
			<Modal.Content className={styles.modalContent} scrolling>
				<div ref={printRef} className={styles.modalContentInner} data-fields={JSON.stringify(bodyFields)}>
					{Object.keys(blockMap).map((block, bi) => {
						const blockFields = blockMap[block];
						const visibleFields = blockFields.filter((f) => {
							return shouldShow(f, fields);
						});
						if (!visibleFields.length) return null;
						return (
							<div key={bi} className={`${styles.blockCard} flow-block`}>
								<h3 className={styles.blockTitle}>
									<Icon name="cube" className={styles.blockTitleIcon} /> {block}
								</h3>
								<table className={styles.fieldTable}>
									<tbody>
										{visibleFields
											.sort((a, b) => {
												return (a.order || 0) - (b.order || 0);
											})
											.map((f, fi) => {
												const iconName = getIcon(f.widget);
												return (
													<tr key={fi} className={styles.fieldRow}>
														<td className={styles.fieldLabel}>
															<span className={styles.fieldLabelInner}>
																{iconName && <Icon name={iconName} size="small" className={styles.fieldLabelIcon} />}
																{f.label}
															</span>
														</td>
														<td className={styles.fieldColon}>:</td>
														<td className={styles.fieldValue}>
															{f.widget === "textarea" ? (
																<span className={styles.fieldValueTextarea}>
																	<FieldValue field={f} />
																</span>
															) : (
																<span className={styles.fieldValueText}>
																	<FieldValue field={f} />
																</span>
															)}
														</td>
													</tr>
												);
											})}
									</tbody>
								</table>
							</div>
						);
					})}
				</div>
			</Modal.Content>
			<Modal.Actions className={styles.modalActions}>
				<Button onClick={onClose} className={styles.btnClose}>
					Cerrar
				</Button>
				<Button onClick={handlePrint} className={styles.btnPrint}>
					<Icon name="print" /> Imprimir
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

FlowPrintModal.propTypes = {
	fields: PropTypes.array,
	title: PropTypes.string,
	onClose: PropTypes.func,
};

const FlowFormModal = ({ item, onClose }) => {
	let fields = [];
	try {
		const raw = JSON.parse(item.text || "{}");
		const i = raw.interactive;
		if (i) {
			fields = [
				{ widget: "header", label: i.header?.text || i.action?.parameters?.flow_cta || "Flow", block: "Formulario", order: 0 },
				{ name: "header", label: "Header", value: i.header?.text || "", widget: "text", order: 1, block: "Mensaje" },
				{ name: "body", label: "Body", value: i.body?.text || "", widget: "textarea", order: 2, block: "Mensaje" },
				{ name: "footer", label: "Footer", value: i.footer?.text || "", widget: "text", order: 3, block: "Mensaje" },
				{ name: "flowId", label: "Flow ID", value: i.action?.parameters?.flow_id || "", widget: "text", order: 4, block: "Flow" },
				{
					name: "screen",
					label: "Screen",
					value: i.action?.parameters?.flow_action_payload?.screen || "",
					widget: "text",
					order: 5,
					block: "Flow",
				},
			];
			const payloadData = i.action?.parameters?.flow_action_payload?.data;
			if (payloadData) {
				Object.keys(payloadData).forEach((k, idx) => {
					fields.push({ name: k, label: k, value: payloadData[k], widget: "text", order: 10 + idx, block: "Data" });
				});
			}
		}
	} catch (e) {
		/* ignore */
	}

	const headerField = fields.find((f) => {
		return f.widget === "header";
	});
	const title = headerField ? headerField.label : "Formulario - Flow";

	return <FlowPrintModal fields={fields} title={title} onClose={onClose} />;
};

FlowFormModal.propTypes = {
	item: PropTypes.object,
	onClose: PropTypes.func,
};

const FlowContent = ({ item }) => {
	const [formModalOpen, setFormModalOpen] = useState(false);

	const interactive = parseInteractive(item.text || "{}");
	if (!interactive) return <div className="msg-text-only">{item.text || ""}</div>;

	const type = interactive.type || "";
	const isFlow = type === "flow";
	const isForm = type === "form";

	const extractFormResponse = () => {
		let formName = "Formulario";
		const fields = [];
		const documents = [];
		const content = Array.isArray(interactive.content) ? interactive.content : [];
		content.forEach((field) => {
			if (field?.widget === "header") {
				if (field?.label) formName = field.label;
				return;
			}
			const widget = String(field?.widget || "").toLowerCase();
			if (["file", "document", "image", "attachment", "signature"].includes(widget)) {
				const hasSource = field?.url ?? field?.value ?? field?.src ?? field?.uri;
				if (hasSource !== null && hasSource !== undefined && hasSource !== "") {
					documents.push(field);
				}
				return;
			}
			fields.push(field);
		});
		let flowId = "";
		try {
			const replyParsed = JSON.parse(item.reply?.text || "{}");
			flowId = replyParsed?.interactive?.action?.parameters?.flow_id || "";
		} catch (e) {
			/* ignore */
		}
		return { formName, flowId, fields, documents };
	};

	const handleFormClick = (e) => {
		e.stopPropagation();
		setFormModalOpen(true);
	};

	const formResponse = isForm ? extractFormResponse() : null;

	return (
		<div>
			{type === "button" && <InteractiveButton interactive={interactive} />}
			{type === "list" && <InteractiveList interactive={interactive} />}
			{type === "product" && <InteractiveProduct interactive={interactive} />}
			{type === "product_list" && <InteractiveProductList interactive={interactive} />}
			{type === "cta_url" && <InteractiveCTAUrl interactive={interactive} />}
			{type === "flow" && <InteractiveFlow interactive={interactive} />}
			{isForm && formResponse && (
				<FlowFormResponse
					formName={formResponse.formName}
					flowId={formResponse.flowId}
					fields={formResponse.fields}
					documents={formResponse.documents}
				/>
			)}
			{!type && !isForm && <div className="msg-text-only">{JSON.stringify(interactive, null, 2)}</div>}
			{isFlow && !item.sentByAgent && (
				<Button basic size="mini" onClick={handleFormClick} style={{ marginTop: 6, fontSize: 11, width: "100%" }}>
					<Icon name="eye" /> Ver formulario
				</Button>
			)}
			{formModalOpen && (
				<FlowFormModal
					item={item}
					onClose={() => {
						return setFormModalOpen(false);
					}}
				/>
			)}
		</div>
	);
};

FlowContent.propTypes = {
	item: PropTypes.object,
};

export default FlowContent;
